import type {
  AnalyticsSdk,
  AnalyticsSdkEventInput,
} from './analytics-sdk.types';
import { getEnvironmentName } from '../utils/utils';
import { generateUUID, createIdentityManager } from './identity-manager';
import { createEventQueue } from './event-queue';
import { createBatcher } from './batcher';
import { createRetryPolicy } from './retry-policy';
import { createTransport } from './transport';

export interface AnalyticsSdkOptions {
  maxAttempts?: number;
  maxEvents?: number;
  batchSize?: number;
  batchIntervalMs?: number;
  transport?: 'websocket' | 'rest';
  tenantId?: string;
}

let preInitBuffer: AnalyticsSdkEventInput[] | null = [];
let currentUserId: string | undefined;

let sdkInstance: AnalyticsSdk = {
  enqueue: (event) => {
    preInitBuffer?.push(event);
  },
  setUserId: () => {},
  clearUserId: () => {},
  shutdown: () => {},
};

export const createAnalyticsSdk = (
  options?: AnalyticsSdkOptions,
): AnalyticsSdk => {
  const identityManager = createIdentityManager();
  const {
    maxAttempts,
    maxEvents,
    batchSize,
    batchIntervalMs,
    transport: transportFlag,
    tenantId,
  } = options ?? {};
  const queue = createEventQueue(maxEvents);
  const transport = createTransport(transportFlag, { tenantId });
  // Convert attempts → retries. Values < 1 clamp to 0 (one attempt, no retries),
  // so a server misconfiguration of 0 or negative degrades gracefully.
  const maxRetries =
    maxAttempts != null && Number.isFinite(maxAttempts)
      ? Math.max(0, maxAttempts - 1)
      : undefined;
  const retryPolicy = createRetryPolicy(transport, maxRetries);
  const normalizedBatchSize =
    batchSize != null && Number.isFinite(batchSize)
      ? Math.max(1, batchSize)
      : undefined;
  const normalizedBatchIntervalMs =
    batchIntervalMs != null && Number.isFinite(batchIntervalMs)
      ? Math.max(1, batchIntervalMs)
      : undefined;
  const batcher = createBatcher(
    queue,
    retryPolicy,
    normalizedBatchSize,
    normalizedBatchIntervalMs,
  );

  return {
    enqueue: (input: AnalyticsSdkEventInput) => {
      const { anonymousId, userId } = identityManager.getIdentityContext();

      queue.enqueue({
        eventName: input.eventName,
        props: {
          ...input.props,
          event_name: input.eventName,
          anonymous_id: anonymousId,
          ...(userId !== undefined && { user_id: userId }),
          timestamp: new Date().toISOString(),
          message_id: generateUUID(),
          page_url: window.location.href,
          page_path: window.location.pathname,
          page_search: window.location.search,
          page_title: document.title,
          page_referrer: document.referrer,
          locale: navigator.language,
          userAgent: navigator.userAgent,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          platform: 'web',
          environment: getEnvironmentName(),
        },
      });

      batcher.notify();
    },

    setUserId: (id: string) => {
      identityManager.setUserId(id);
    },

    clearUserId: () => {
      identityManager.clearUserId();
    },

    shutdown: () => {
      batcher.destroy();
    },
  };
};

export const initAnalyticsSdk = (options?: AnalyticsSdkOptions): void => {
  sdkInstance.shutdown();
  sdkInstance = createAnalyticsSdk(options);

  // Re-apply the authenticated user to the new identity manager so events
  // queued immediately after re-init are not missing user_id.
  if (currentUserId !== undefined) {
    sdkInstance.setUserId(currentUserId);
  }

  const buffered = preInitBuffer ?? [];
  preInitBuffer = null;
  buffered.forEach((event) => sdkInstance.enqueue(event));
};

export const analyticsSdk: AnalyticsSdk = {
  enqueue: (event) => sdkInstance.enqueue(event),
  setUserId: (id) => {
    currentUserId = id;
    sdkInstance.setUserId(id);
  },
  clearUserId: () => {
    currentUserId = undefined;
    sdkInstance.clearUserId();
  },
  shutdown: () => sdkInstance.shutdown(),
};
